// packages/web/app/start.ts
import type {User} from '@supabase/supabase-js';
import {createStart, createMiddleware} from '@tanstack/react-start';
import {authStore} from '@/helpers/authStore.server';
import {buildSupabaseClient} from '@/helpers/supabaseServer.server';

/**
 * Global request middleware: seeds a single Supabase server client and
 * a single resolved user per outer SSR request. All nested server
 * functions invoked during the render inherit the scope via
 * `authStore.getStore()` — one `getUser()`, one (at-most) `setAll`, two
 * `Set-Cookie` headers on the outer response (vs. four clients / eight
 * `Set-Cookie` headers that produced WAY-282's 502).
 *
 * The re-entry guard handles TanStack router issue #5239: request
 * middleware may fire multiple times within a single outer request. On
 * re-entry we inherit the outer scope rather than overwrite it — the
 * owner client remains the only cookie writer.
 */
const requestAuthScope = createMiddleware({type: 'request'}).server(
  async ({next}) => {
    if (authStore.getStore()) {
      return next();
    }

    const supabase = await buildSupabaseClient();
    let user: User | null = null;
    try {
      const {data, error} = await supabase.auth.getUser();
      if (error) {
        console.error('[auth-scope] getUser returned error', {
          code: error.code,
          status: error.status,
        });
      } else {
        user = data.user;
      }
    } catch (error) {
      console.error('[auth-scope] getUser threw', {error});
    }

    return authStore.run({supabase, user}, () => next());
  }
);

export const startInstance = createStart(() => ({
  requestMiddleware: [requestAuthScope],
}));
